// P3-023: Battle top status bar
// Project name, turn counter, HP progress bar
// Flashes red when <= 3 turns remain

import { useEffect, useState } from 'react';
import { ProgressBar } from '@/shared/ui/ProgressBar';
import { useBattleStore } from '../stores/battleStore';
import { EventBus } from '@/shared/game/EventBus';

interface ProjectStatusProps {
  projectName: string;
}

export function ProjectStatus({ projectName }: ProjectStatusProps) {
  const currentTurn = useBattleStore((s) => s.currentTurn);
  const maxTurns = useBattleStore((s) => s.maxTurns);
  const enemy = useBattleStore((s) => s.enemy);

  const [displayHp, setDisplayHp] = useState(enemy?.hp ?? 0);
  const [displayMaxHp, setDisplayMaxHp] = useState(enemy?.maxHp ?? 0);

  const turnsRemaining = maxTurns - currentTurn;
  const isDanger = turnsRemaining <= 3 && turnsRemaining > 0;

  // Listen for turnEnd to update HP display
  useEffect(() => {
    const handleTurnEnd = (data: { turn: number; enemyHp: number; maxHp: number }) => {
      setDisplayHp(data.enemyHp);
      setDisplayMaxHp(data.maxHp);
    };

    EventBus.on('turnEnd', handleTurnEnd);
    return () => {
      EventBus.off('turnEnd', handleTurnEnd);
    };
  }, []);

  // Sync initial values
  useEffect(() => {
    if (enemy) {
      setDisplayHp(enemy.hp);
      setDisplayMaxHp(enemy.maxHp);
    }
  }, [enemy]);

  const hpPercent = displayMaxHp > 0 ? Math.round((displayHp / displayMaxHp) * 100) : 0;

  // Determine HP bar color
  let hpColor = 'var(--color-danger)';
  if (hpPercent > 66) {
    hpColor = 'var(--color-success)';
  } else if (hpPercent > 33) {
    hpColor = 'var(--color-warning)';
  }

  return (
    <div className="project-status">
      <span className="project-status__name">{projectName}</span>

      <span
        className={`project-status__turn ${isDanger ? 'project-status__turn--danger' : ''}`}
      >
        Remaining: {turnsRemaining}/{maxTurns}
      </span>

      <div className="project-status__hp-section">
        <span className="project-status__hp-label">
          HP: {Math.max(0, Math.round(displayHp))}/{displayMaxHp} [{hpPercent}%]
        </span>
        <div className="project-status__hp-bar">
          <ProgressBar
            value={displayHp}
            max={displayMaxHp}
            color={hpColor}
            showPercent
          />
        </div>
      </div>
    </div>
  );
}
